// The desktop "Send all dispatch texts" modal — dispatch preview on top, a
// "no work tomorrow" picker below, both sent in one pass.
//
// Replaces the window.confirm() that used to stand in for the dispatch preview. The
// reason it grew into a real modal: on a rain-out or a light day the crew who are NOT on
// the board get nothing and call the office, and Patrick wants to text them from the same
// screen — but not always everybody, so it needs checkboxes, which a confirm dialog can't
// give. The dispatch half sends exactly what it always did.
//
// ⚠ THE TWO HALVES ARE INDEPENDENT AND EITHER CAN BE TURNED OFF. "Send dispatch texts"
// unchecks so a no-work notice can go out on a day whose dispatch already went this
// morning; "Send 'no work' notice" is off until somebody is ticked.
//
// ⚠ DESKTOP ONLY, DELIBERATELY. MobileDashboard's own "Send all dispatch texts" button
// keeps its existing window.confirm flow — the divergence is intentional, not an oversight.
// If the no-work picker is ever wanted on mobile, this component is the thing to reuse
// rather than a second implementation of the same rules.
//
// Server: /api/text/dispatch (unchanged) and /api/text/no-work. The client's selection is
// only ever a list of opaque keys — the server re-resolves consent AND the live schedule
// before sending, so a stale tab can neither text somebody who has revoked consent nor
// tell somebody there is no work after they were added to that day's board.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { dispatchAllTexts, loadNoWorkCandidates, sendNoWorkNotice } from '../lib/db'

// Mirrors segmentInfo() in api/lib/announce-audience.js so the office sees the real
// message count while typing. The HEADER and FOOTER it wraps are served by the API rather
// than re-typed here, so the count and the server's billing can't drift.
const GSM = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"
const GSM_EXT = '^{}\\[~]|€'
function segmentInfo(text) {
  const s = String(text || '')
  const unicode = [...s].some(c => !GSM.includes(c) && !GSM_EXT.includes(c))
  const units = unicode ? [...s].length : [...s].reduce((n, c) => n + (GSM_EXT.includes(c) ? 2 : 1), 0)
  const single = unicode ? 70 : 160
  const multi = unicode ? 67 : 153
  return { units, unicode, segments: units === 0 ? 0 : units <= single ? 1 : Math.ceil(units / multi) }
}

function fmtPhone(p) {
  const d = String(p || '').replace(/\D/g, '')
  const ten = d.length === 11 && d[0] === '1' ? d.slice(1) : d
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(p || '')
}

function fmtWorked(ymd) {
  if (!ymd) return 'never'
  const [y, m, d] = String(ymd).split('-').map(Number)
  if (!y || !m || !d) return String(ymd)
  return new Date(y, m - 1, d, 12, 0, 0).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
}

const REASON_LABEL = {
  no_optin: 'Not opted in',
  no_phone: 'No phone on file',
  already_notified: 'Already texted',
}

const sx = {
  section: { border: '0.5px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 },
  h: { fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 6 },
  sub: { fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 },
  warn: { fontSize: 11, color: '#92510a', background: '#fff4e5', border: '0.5px solid #f0d3a5', borderRadius: 6, padding: '6px 9px', marginTop: 6 },
  wrap: { background: 'var(--surface2)', color: 'var(--text3)', fontSize: 11, padding: '4px 8px', borderRadius: 4, whiteSpace: 'pre-wrap' },
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px', borderBottom: '0.5px solid var(--border)', fontSize: 12 },
}

export default function DispatchSendModal({ dateYmd, dayLabel, onClose, onDone }) {
  const [phase, setPhase] = useState('loading')   // loading | ready | error
  const [errMsg, setErrMsg] = useState('')
  const [dispatch, setDispatch] = useState(null)  // the /api/text/dispatch preview payload
  const [nw, setNw] = useState(null)              // the /api/text/no-work GET payload

  const [sendDispatch, setSendDispatch] = useState(true)
  const [sendNoWork, setSendNoWork] = useState(false)
  const [picked, setPicked] = useState(() => new Set())
  const [body, setBody] = useState('')
  const [query, setQuery] = useState('')

  const [resend, setResend] = useState(false)     // deliberately re-text the already-texted
  const [confirm, setConfirm] = useState(null)    // the no-work preview awaiting a 2nd click
  const [busy, setBusy] = useState(false)
  // The confirm panel is too tall for the footer, so it stays in the scroller and
  // gets scrolled to instead — otherwise the first Send click looks like a no-op
  // on a long job/crew list.
  const confirmRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const [d, n] = await Promise.all([
        dispatchAllTexts(dateYmd, { preview: true }),
        loadNoWorkCandidates(dateYmd),
      ])
      if (cancelled) return
      // The dispatch preview is the load-bearing half — without it there is nothing to
      // show. A no-work failure is survivable: the dispatch half still works, and the
      // error is named rather than leaving an empty list looking like "nobody to text".
      if (d.error) {
        setErrMsg(`Preview failed: ${d.error}${d.detail ? ' — ' + d.detail : ''}`)
        setPhase('error'); return
      }
      setDispatch(d)
      setSendDispatch((d.job_count || 0) > 0)
      if (n.error) {
        setErrMsg(`No-work list unavailable: ${n.error}${n.detail ? ' — ' + n.detail : ''}`)
      } else {
        setNw(n)
        setBody(n.default_body || '')
        setPicked(new Set((n.candidates || []).filter(c => c.preselect).map(c => c.key)))
      }
      setPhase('ready')
    })()
    return () => { cancelled = true }
  }, [dateYmd])

  useEffect(() => {
    if (confirm && confirmRef.current) confirmRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [confirm])

  const candidates = (nw && nw.candidates) || []
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return candidates
    return candidates.filter(c =>
      String(c.name).toLowerCase().includes(q) ||
      String(c.local || '').toLowerCase().includes(q))
  }, [candidates, query])

  const fullBody = nw ? `${nw.header || ''}${body.trim()}\n\n${nw.footer || ''}` : body
  const seg = segmentInfo(fullBody)
  const pickedCount = picked.size

  // Already-texted people who are currently ticked. Drives the resend opt-in: it is
  // surfaced ONLY when the choice is actually live. Declared BEFORE setPickedAnd, which
  // reads it.
  const alreadyByKey = useMemo(
    () => new Map(candidates.filter(c => c.already_notified).map(c => [c.key, c])),
    [candidates])
  const pickedAlready = [...picked].map(k => alreadyByKey.get(k)).filter(Boolean)

  // Ticking anybody arms the notice; clearing everyone disarms it, so the Send label can
  // never promise a notice to nobody.
  const setPickedAnd = (next) => {
    setPicked(next)
    setSendNoWork(next.size > 0)
    setConfirm(null)
    // Un-ticking the last already-texted person disarms the resend, so the opt-in can
    // never linger and silently apply to a later, different selection.
    if (![...next].some(k => alreadyByKey.has(k))) setResend(false)
  }
  const toggle = (key) => {
    const next = new Set(picked)
    if (next.has(key)) next.delete(key); else next.add(key)
    setPickedAnd(next)
  }
  const selectAll = (list) => setPickedAnd(new Set(list.filter(c => c.eligible).map(c => c.key)))

  const dispatchArmed = sendDispatch && dispatch && (dispatch.job_count || 0) > 0
  const noWorkArmed = sendNoWork && pickedCount > 0 && body.trim().length > 0
  const canSend = !busy && (dispatchArmed || noWorkArmed)

  const sendLabel = () => {
    const bits = []
    if (dispatchArmed) bits.push(`${dispatch.job_count} job${dispatch.job_count === 1 ? '' : 's'} (${dispatch.crew_to_text} crew)`)
    if (noWorkArmed) bits.push(`no-work notice to ${pickedCount}`)
    if (!bits.length) return 'Nothing selected'
    return `Send — ${bits.join(' + ')}`
  }

  // FIRST click resolves the no-work half server-side and shows what it actually costs;
  // the SECOND click sends. The dispatch half was already previewed on open.
  const onPrimary = async () => {
    if (busy) return
    if (!confirm) {
      if (!noWorkArmed) { setConfirm({ skip: true }); return }
      setBusy(true)
      const p = await sendNoWorkNotice(dateYmd, [...picked], body, { preview: true, resend })
      setBusy(false)
      if (p.error) { setErrMsg(`Could not price the notice: ${p.error}${p.detail ? ' — ' + p.detail : ''}`); return }
      setErrMsg('')
      setConfirm(p)
      return
    }
    await doSend()
  }

  const doSend = async () => {
    setBusy(true)
    const parts = []
    let anyError = false

    // Dispatch FIRST — it is the time-critical one. A dispatch failure does NOT skip the
    // notice: both outcomes are reported so a half-send is never silent.
    if (dispatchArmed) {
      const r = await dispatchAllTexts(dateYmd)
      if (r.error) {
        anyError = true
        parts.push(`Dispatch FAILED: ${r.error}${r.detail ? ' — ' + r.detail : ''}`)
      } else {
        const sentJobs = (r.jobs || []).filter(j => j.status === 'sent').length
        const skippedJobs = (r.jobs || []).filter(j => j.status === 'skipped_already_sent').length
        if (r.texts_failed) anyError = true
        parts.push(
          `Sent ${r.texts_sent} dispatch text${r.texts_sent === 1 ? '' : 's'} across ${sentJobs} job${sentJobs === 1 ? '' : 's'}` +
          `${skippedJobs ? `, ${skippedJobs} skipped (already sent)` : ''}` +
          `${r.texts_failed ? ` — ⚠ ${r.texts_failed} failed` : ''}` +
          `${(r.skipped_no_optin && r.skipped_no_optin.length) ? ` — ⚠ ${r.skipped_no_optin.length} NOT texted (no opt-in yet): ${r.skipped_no_optin.join(', ')}` : ''}`
        )
      }
    }

    if (noWorkArmed) {
      const r = await sendNoWorkNotice(dateYmd, [...picked], body, { resend })
      if (r.error) {
        anyError = true
        parts.push(`No-work notice FAILED: ${r.error}${r.detail ? ' — ' + r.detail : ''}`)
      } else {
        if (r.failed || r.not_sent) anyError = true
        parts.push(
          `Sent ${r.sent} no-work notice${r.sent === 1 ? '' : 's'}` +
          `${r.resent_count ? ` (${r.resent_count} re-texted)` : ''}` +
          `${r.failed ? ` — ⚠ ${r.failed} failed` : ''}` +
          `${r.not_sent ? ` — ⚠ ${r.not_sent} not sent (ran out of time)` : ''}` +
          `${(r.skipped && r.skipped.length) ? ` — ${r.skipped.length} skipped: ${r.skipped.map(s => `${s.name} (${REASON_LABEL[s.reason] || s.reason})`).join(', ')}` : ''}`
        )
      }
    }

    setBusy(false)
    onDone({ kind: anyError ? 'error' : 'success', msg: parts.join(' · ') || 'Nothing was sent.' })
    onClose()
  }

  const skipSent = ((dispatch && dispatch.skipped_already_sent) || []).map(x => x.job_number).filter(Boolean)
  const skipNone = ((dispatch && dispatch.skipped_no_crew) || []).map(x => x.job_number).filter(Boolean)

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      {/* overflowY hidden: .modal sets auto, which would double-scroll against the inner
          scroller below. Same reason as the announcement review modal. */}
      <div className="modal" onClick={e => e.stopPropagation()}
        style={{ maxWidth: 720, width: '94%', maxHeight: 'calc(100vh - 100px)', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-title">Send texts for {dayLabel}</div>
        <div className="modal-sub">Crew dispatch and the "no work" notice go out together.</div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {phase === 'loading' && <div style={{ padding: 20, fontSize: 13, color: 'var(--text3)' }}>Loading…</div>}
          {phase === 'error' && <div style={{ padding: 20, fontSize: 13, color: 'var(--red-text, #A12622)' }}>{errMsg}</div>}

          {phase === 'ready' && (
            <>
              {/* ── Dispatch ─────────────────────────────────────────────── */}
              <div style={sx.section}>
                <div style={sx.h}>Dispatch</div>
                {dispatch.job_count ? (
                  <div style={sx.sub}>
                    <div><b>{dispatch.job_count} job{dispatch.job_count === 1 ? '' : 's'} · {dispatch.crew_to_text} crew will be texted</b></div>
                    {dispatch.crew_no_optin > 0 && (
                      <div style={sx.warn}>⚠ {dispatch.crew_no_optin} will NOT be texted — no opt-in yet: {(dispatch.no_optin_names || []).join(', ')}</div>
                    )}
                    {skipSent.length > 0 && <div>{skipSent.length} job{skipSent.length === 1 ? '' : 's'} skipped (already sent): {skipSent.join(', ')}</div>}
                    {skipNone.length > 0 && <div>{skipNone.length} job{skipNone.length === 1 ? '' : 's'} skipped (no crew assigned): {skipNone.join(', ')}</div>}
                  </div>
                ) : (
                  <div style={sx.sub}>
                    Nothing to dispatch for {dayLabel}
                    {skipSent.length ? ` — ${skipSent.length} already sent (${skipSent.join(', ')})` : ''}
                    {skipNone.length ? `${skipSent.length ? ';' : ' —'} ${skipNone.length} with no crew` : ''}.
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, fontSize: 12, color: 'var(--text)', cursor: dispatch.job_count ? 'pointer' : 'not-allowed', opacity: dispatch.job_count ? 1 : 0.5 }}>
                  <input type="checkbox" checked={sendDispatch && dispatch.job_count > 0} disabled={!dispatch.job_count || busy}
                    onChange={e => { setSendDispatch(e.target.checked); setConfirm(null) }} />
                  Send dispatch texts
                </label>
              </div>

              {/* ── No work notice ───────────────────────────────────────── */}
              {nw && (
                <div style={sx.section}>
                  <div style={sx.h}>Not scheduled for {nw.date_label} — {nw.counts.total} crew</div>

                  <textarea
                    value={body} maxLength={nw.max_body_chars}
                    onChange={e => { setBody(e.target.value); setConfirm(null) }}
                    rows={3} disabled={busy}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, lineHeight: 1.5, padding: 8, borderRadius: 6, border: '0.5px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', resize: 'vertical', marginTop: 4 }} />
                  <div style={{ marginTop: 4 }}>
                    <div style={sx.wrap}>{nw.header}<i>your message</i></div>
                    <div style={{ ...sx.wrap, marginTop: 3 }}>{nw.footer}</div>
                  </div>
                  <div style={{ ...sx.sub, marginTop: 5 }}>
                    {seg.units} char{seg.units === 1 ? '' : 's'} · {seg.segments} segment{seg.segments === 1 ? '' : 's'} each
                    {pickedCount > 0 && <> · {pickedCount * seg.segments} message{pickedCount * seg.segments === 1 ? '' : 's'} total</>}
                    {seg.unicode && <span style={{ color: '#92510a' }}> · ⚠ non-GSM character — segments drop from 160 to 70 chars, doubling the cost</span>}
                  </div>

                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0 6px' }}>
                    <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name / local" disabled={busy}
                      style={{ flex: 1, minWidth: 160, fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '0.5px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)' }} />
                    <button className="btn btn-sm" disabled={busy} onClick={() => selectAll(candidates.filter(c => c.recent && !c.already_notified))}>Select all recent</button>
                    {/* Excludes the already-texted, matching "Select all recent". A resend
                        must always be a per-person choice, never swept in by a bulk button. */}
                    <button className="btn btn-sm" disabled={busy} onClick={() => selectAll(candidates.filter(c => c.eligible && !c.already_notified))}>Select all</button>
                    <button className="btn btn-sm" disabled={busy} onClick={() => setPickedAnd(new Set())}>Clear</button>
                  </div>

                  <div style={{ maxHeight: 260, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    {shown.length === 0 && <div style={{ padding: 14, fontSize: 12, color: 'var(--text3)' }}>Nobody matches.</div>}
                    {shown.map(c => {
                      const disabled = !c.eligible || busy
                      return (
                        <label key={c.key} style={{ ...sx.row, opacity: c.eligible ? 1 : 0.55, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                          <input type="checkbox" checked={picked.has(c.key)} disabled={disabled} onChange={() => toggle(c.key)} />
                          <span style={{ fontWeight: c.recent ? 600 : 400, color: 'var(--text)' }}>{c.name}</span>
                          <span style={{ color: 'var(--text3)', fontSize: 11 }}>
                            {c.local || ''}
                          </span>
                          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11 }}>
                            <span style={{ color: c.recent ? 'var(--text2)' : 'var(--text3)' }}>Last worked: {fmtWorked(c.last_worked)}</span>
                            {c.already_notified && <span style={{ color: '#92510a', background: '#fff4e5', borderRadius: 4, padding: '1px 6px' }}>Already texted</span>}
                            {!c.eligible && <span style={{ color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 4, padding: '1px 6px' }}>{REASON_LABEL[c.reason] || c.reason}</span>}
                          </span>
                        </label>
                      )
                    })}
                  </div>

                  <div style={{ ...sx.sub, marginTop: 5 }}>
                    {nw.counts.preselected} preselected (worked in the last 7 days)
                    {nw.counts.no_optin ? ` · ${nw.counts.no_optin} not opted in` : ''}
                    {nw.counts.no_phone ? ` · ${nw.counts.no_phone} with no phone on file` : ''}
                    {nw.counts.already_notified ? ` · ${nw.counts.already_notified} already texted about this day` : ''}
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, fontSize: 12, color: 'var(--text)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={sendNoWork} disabled={busy}
                      onChange={e => { setSendNoWork(e.target.checked); setConfirm(null) }} />
                    Send "no work" notice{pickedCount ? ` to ${pickedCount}` : ''}
                  </label>

                  {/* The resend opt-in appears ONLY when an already-texted person is
                      actually ticked, so it is never a standing switch someone forgets is
                      on. Without it those picks are reported as skipped, exactly as before. */}
                  {pickedAlready.length > 0 && (
                    <div style={sx.warn}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={resend} disabled={busy}
                          onChange={e => { setResend(e.target.checked); setConfirm(null) }} />
                        Text the {pickedAlready.length} already-texted again
                      </label>
                      <div style={{ marginTop: 3, fontWeight: 400 }}>
                        {pickedAlready.map(c => c.name).join(', ')}
                        {!resend && ' — will be skipped unless you tick this.'}
                      </div>
                    </div>
                  )}

                  {nw.outside_quiet_hours && (
                    <div style={sx.warn}>
                      ⚠ It is outside 7am-8pm ET. Sending now is allowed (a 5am snow-day notice is legitimate) — just make sure you mean it.
                    </div>
                  )}
                </div>
              )}

              {/* ── What the second click will actually do ────────────────── */}
              {confirm && !confirm.skip && (
                <div ref={confirmRef} style={{ ...sx.section, borderColor: '#bfe3c8', background: 'var(--green-bg, #EAF7EE)' }}>
                  <div style={sx.h}>Confirm — press Send again</div>
                  <div style={sx.sub}>
                    <div><b>{confirm.recipient_count}</b> will get the no-work notice · {confirm.segments} segment{confirm.segments === 1 ? '' : 's'} each · <b>{confirm.total_messages}</b> message{confirm.total_messages === 1 ? '' : 's'}</div>
                    {confirm.skipped && confirm.skipped.length > 0 && (
                      <div style={sx.warn}>
                        ⚠ {confirm.skipped.length} of your picks will be SKIPPED: {confirm.skipped.map(s => `${s.name} (${REASON_LABEL[s.reason] || s.reason})`).join(', ')}
                      </div>
                    )}
                    {confirm.resent_count > 0 && (
                      <div style={sx.warn}>
                        ⚠ {confirm.resent_count} of these were already texted about this day and WILL be texted again.
                      </div>
                    )}
                    {confirm.unknown_keys > 0 && (
                      <div style={sx.warn}>
                        ⚠ {confirm.unknown_keys} of your picks are no longer on the no-work list — most likely they were just SCHEDULED for that day. They will not be texted.
                      </div>
                    )}
                  </div>
                  <div style={{ ...sx.wrap, marginTop: 6 }}>{confirm.full_body}</div>
                </div>
              )}
              {confirm && confirm.skip && (
                <div ref={confirmRef} style={{ ...sx.section, borderColor: '#bfe3c8', background: 'var(--green-bg, #EAF7EE)' }}>
                  <div style={sx.h}>Confirm — press Send again</div>
                  <div style={sx.sub}>Dispatch texts only. No "no work" notice will be sent.</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Outside the scroller on purpose: in the body this lands below the fold on a
            long list and a failed send reads as nothing happening. The phase==='error'
            branch above already renders errMsg as the whole body — hence the ready guard,
            so it is never shown twice. */}
        {phase === 'ready' && errMsg && <div style={{ ...sx.warn, marginTop: 10 }}>{errMsg}</div>}

        <div className="modal-actions">
          <button className="btn btn-primary btn-sm" disabled={!canSend} onClick={onPrimary}>
            {busy ? 'Working…' : confirm ? 'Send now' : sendLabel()}
          </button>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
